import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { IState } from 'redux/state';
import {
  fetchInitialProjects,
  insertInferredProjectPermissions,
  searchProjects,
} from '~redux/modules/projects.actions';
import {
  getAllLoadedProjects,
  getEnrichedProjectsForCurrentUser,
  getIsLoadingProjects,
  getPinnedProjectsForProjectDropdown,
  getProjectPermissionsMap,
  getRecentlyUsedProjects,
} from '~redux/selectors/projects';
import { getIsUserAdmin, getUserId } from '~redux/selectors/user';
import type { ThunkDispatch } from '~types/redux';
import { getProjectColor } from '~utils/project';
import { assignErrorMessage } from './projectAssignmentError';
import { hasDisplayableProject } from './projectSuggestionChoices';

export { assignErrorMessage } from './projectAssignmentError';

import { AgentProjectPopover, type ProjectOption } from './AgentProjectPopover';
import { AgentProjectPredictions, predictionIndicator } from './AgentProjectPredictions';
import type { AgentPeriod } from './agentTracking.model';
import { useSessionProjectSuggestions } from './SessionProjectSuggestions';
import { useAgentProjectCreation } from './useAgentProjectCreation';

/** Owners whose projects this page already asked for: a board of many owners asks once each. */
const requestedOwners = new Set<number>();

/**
 * Load a colleague's projects, as the Hours page does when acting as them, so a picker for
 * their period can offer what they are on (TT-6346). Once per owner per page load; a failed
 * load is tried again on the next open. Either way the owner gets a project list in the
 * store, empty when they are on none, so a picker never waits on one that won't come.
 */
export function requestOwnerProjects(
  dispatch: ThunkDispatch,
  ownerId: number,
  load: typeof fetchInitialProjects = fetchInitialProjects
): Promise<void> {
  if (requestedOwners.has(ownerId)) return Promise.resolve();
  requestedOwners.add(ownerId);
  return Promise.resolve(dispatch(load(ownerId)))
    .catch(() => {
      requestedOwners.delete(ownerId);
    })
    .then(() => {
      dispatch(insertInferredProjectPermissions(ownerId, []));
    });
}

interface Props {
  value: number | null;
  suggestedProjectId?: number;
  partialAssignment?: { projectId: number; unassignedCount: number };
  disabled?: boolean;
  placeholder: string;
  onChange: (projectId: number | null, segmentIds?: number[]) => Promise<void>;
  ariaLabel: string;
  periods?: AgentPeriod[];
  onAssignPeriod?: (period: AgentPeriod, id: number | null) => Promise<void>;
  appearance?: 'pill' | 'button';
  /** Whose periods the project goes on, when no periods are given (a bulk bar, a dialog). */
  ownerId?: number;
}
export default function AgentProjectPicker({
  value,
  placeholder,
  onChange,
  ariaLabel,
  suggestedProjectId,
  partialAssignment,
  disabled,
  periods,
  onAssignPeriod,
  appearance,
  ownerId: givenOwnerId,
}: Props) {
  const dispatch = useDispatch<ThunkDispatch>();
  const creation = useAgentProjectCreation(onChange);
  const userId = useSelector(getUserId);
  const projects = useSelector(getEnrichedProjectsForCurrentUser);
  const pinned = useSelector(getPinnedProjectsForProjectDropdown);
  const recent = useSelector(getRecentlyUsedProjects);
  const loading = useSelector(getIsLoadingProjects);
  const isAdmin = useSelector(getIsUserAdmin);
  const allProjects = useSelector(getAllLoadedProjects);
  // A colleague's period (TT-6346): the projects the owner is on that the viewer may use
  // too, as Rails requires; an admin may use any of the owner's.
  const ownerId = givenOwnerId ?? periods?.[0]?.userId ?? null;
  const foreignOwner = ownerId !== null && ownerId !== userId ? ownerId : null;
  const ownerProjectIds = useSelector((state: IState) =>
    foreignOwner === null ? undefined : getProjectPermissionsMap(state)[foreignOwner]
  );
  const ownerProjects = useMemo(
    () => (ownerProjectIds ? new Set(ownerProjectIds) : null),
    [ownerProjectIds]
  );
  const loadOwnerProjects = useCallback(() => {
    if (foreignOwner !== null) void requestOwnerProjects(dispatch, foreignOwner);
  }, [dispatch, foreignOwner]);
  const review = useSessionProjectSuggestions();
  const timer = useRef<ReturnType<typeof setTimeout>>();
  useEffect(() => () => clearTimeout(timer.current), []);
  const onSearch = useCallback(
    (query: string) => {
      clearTimeout(timer.current);
      timer.current = setTimeout(() => {
        const searchAs = foreignOwner ?? userId;
        if (searchAs) void dispatch(searchProjects(query, searchAs)).catch(() => undefined);
      }, 250);
    },
    [dispatch, userId, foreignOwner]
  );
  const options: ProjectOption[] = [];
  const seen = new Set<number>();
  const usable = (id: number) => foreignOwner === null || !!ownerProjects?.has(id);
  for (const [group, items] of [
    ['Pinned', pinned],
    ['Recent', recent],
    ['All projects', foreignOwner !== null && isAdmin ? allProjects : projects],
  ] as const) {
    for (const { base } of items) {
      if (seen.has(base.id) || !usable(base.id)) continue;
      seen.add(base.id);
      options.push({
        id: base.id,
        name: base.name,
        client: base.client?.name,
        color: getProjectColor(base),
        group,
        disabled: !base.active,
      });
    }
  }
  // A period picker should name its saved suggestion just like the session picker.
  const periodSuggestion =
    value === null && periods?.length === 1 ? review?.suggestions[periods[0].segmentId] : undefined;
  const shownSuggestionId =
    suggestedProjectId ??
    (periodSuggestion && hasDisplayableProject(periodSuggestion)
      ? (periodSuggestion.project_id ?? undefined)
      : undefined);
  const target = partialAssignment?.projectId ?? shownSuggestionId ?? value;
  // A colleague's period can sit on a project the viewer may not pick, or not even load
  // (Rails names it on the period then); it still shows its name.
  const known = target === null ? undefined : allProjects.find(p => p.base.id === target);
  const named =
    target === null ? undefined : periods?.find(p => p.projectId === target && p.projectName);
  const project =
    options.find(p => p.id === target) ??
    (known && {
      id: known.base.id,
      name: known.base.name,
      color: getProjectColor(known.base),
      disabled: true,
    }) ??
    (named?.projectName
      ? {
          id: target!,
          name: named.projectName,
          color: named.projectColor ?? undefined,
          disabled: true,
        }
      : undefined);
  const label = project?.name ?? placeholder;
  const isSuggestion = value === null && !partialAssignment && shownSuggestionId !== undefined;
  const hasSuggestion =
    isSuggestion ||
    periods?.some(period => {
      const suggestion = review?.suggestions[period.segmentId];
      return suggestion && hasDisplayableProject(suggestion);
    });
  const unassigned = periods?.filter(p => p.projectId === null) ?? [];
  const incomplete =
    periods && unassigned.length > 0 && unassigned.length < periods.length
      ? {
          count: unassigned.length,
          total: periods.length,
          periods: periods.map(period => ({
            id: period.segmentId,
            projectId: period.projectId,
            time: period.start && period.end ? `${period.start}–${period.end}` : 'Time unavailable',
            date: period.date,
            pendingLabel:
              period.projectPredictionState === 'waiting_summary'
                ? 'Waiting for summary'
                : ['queued', 'running'].includes(period.projectPredictionState ?? '')
                  ? 'Finding project…'
                  : undefined,
          })),
          projectName: partialAssignment ? project?.name : undefined,
          complete:
            partialAssignment && project && !project.disabled
              ? async () => {
                  await onChange(
                    partialAssignment.projectId,
                    unassigned.map(period => period.segmentId)
                  );
                }
              : undefined,
        }
      : undefined;
  const predictionPeriods = incomplete ? unassigned : periods;
  const allPending =
    !!periods?.length &&
    periods.every(
      p =>
        p.projectId === null &&
        ['waiting_summary', 'queued', 'running'].includes(p.projectPredictionState ?? '')
    );
  const pendingLabel = periods?.some(p =>
    ['queued', 'running'].includes(p.projectPredictionState ?? '')
  )
    ? 'Finding project…'
    : 'Waiting for summary';
  return (
    <AgentProjectPopover
      value={value}
      incomplete={incomplete}
      predictionBusy={!!review?.busy}
      predictionsInSummary={
        !!incomplete &&
        unassigned.some(p => {
          const suggestion = review?.suggestions[p.segmentId];
          return !!suggestion && hasDisplayableProject(suggestion);
        })
      }
      revealOnHover={
        value === null &&
        !incomplete &&
        !hasSuggestion &&
        !periods?.some(p =>
          ['waiting_summary', 'queued', 'running'].includes(p.projectPredictionState ?? '')
        ) &&
        !partialAssignment &&
        !periods?.some(p => p.projectId !== null)
      }
      creation={foreignOwner === null ? creation : undefined}
      appearance={appearance}
      label={allPending && !isSuggestion ? pendingLabel : label}
      suggested={isSuggestion}
      projectColor={project?.color}
      ariaLabel={ariaLabel}
      options={options}
      loading={loading || (foreignOwner !== null && !ownerProjects)}
      disabled={disabled}
      indicator={
        periods
          ? predictionIndicator(predictionPeriods ?? [], review)
          : suggestedProjectId
            ? { symbol: '✧', label: 'Suggested project' }
            : undefined
      }
      predictions={
        periods && (
          <AgentProjectPredictions
            periods={predictionPeriods ?? []}
            compact={!!incomplete}
            projects={options}
            review={review}
            onAssignPeriod={onAssignPeriod}
          />
        )
      }
      onSearch={onSearch}
      onOpen={loadOwnerProjects}
      onChange={async id => {
        try {
          await onChange(id);
        } catch (error) {
          throw new Error(assignErrorMessage(error));
        }
      }}
    />
  );
}
